import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

import { EVALUATIONS_FILTERS } from "../../../assets/constants"
import axios from "../../../utils/api-client"
import { blobToBase64 } from "../utils/signature-format"
import {
	AcknowledgeFormError,
	AcknowledgeFormPayload,
	AcknowledgeFormResponse,
	AcknowledgementListResponse,
	CalculateScorePayload,
	CalculateScoreResponse,
	EvaluationsState,
	InstanceFormDetail,
	ProgramInstanceDetail,
	SaveAdditionalFieldAnswersPayload,
	SaveFormAnswersPayload,
	SaveFormAnswersResponse,
	UpdateInstanceDatesPayload,
	UpdateInstanceDatesResponse,
	WorkerProgram,
	WorkerRow,
} from "./evaluations-types"

const initialState: EvaluationsState = {
	workers: {
		status: "idle",
		data: [],
		totalCount: 0,
		error: "",
	},
	evaluationFilters: {
		status: "idle",
		data: {
			locations: [],
			workCenters: [],
			managers: [],
			filterRelationships: [],
		},
		error: "",
	},
	workerPrograms: {
		status: "idle",
		data: [],
		error: "",
	},
	programInstanceDetail: {
		status: "idle",
		data: null,
		error: "",
	},
	searchText: "",
	adjustDatesDialog: {
		isOpen: false,
		instance: null,
		workerName: null,
		saveStatus: "idle",
		error: null,
	},
	fillFormDialog: {
		isOpen: false,
		instanceFormId: null,
		workerName: null,
		workCenterName: null,
		detail: null,
		status: "idle",
		saveStatus: "idle",
		saveAction: null,
		saveError: null,
		forceReadOnly: false,
		counterpartInstanceFormId: null,
		counterpartDetail: null,
		counterpartStatus: "idle",
		scoreCalculation: {
			status: "idle",
			score: null,
			targetValue: null,
			metTarget: null,
		},
	},
	saveAdditionalFieldsStatus: "idle",
	myAcknowledgements: {
		status: "idle",
		data: [],
		totalCount: 0,
		error: "",
	},
	ackRowTypes: {},
	acknowledgeDialog: {
		isOpen: false,
		instanceFormId: null,
		workerName: null,
		detail: null,
		status: "idle",
		saveStatus: "idle",
		isSigned: false,
		saveErrorStatus: null,
	},
}

export const fetchWorkers = createAsyncThunk(
	"evaluations/fetchWorkers",
	async (_, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get<{ workers: WorkerRow[]; totalCount: number }>(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/GetWorkers`,
				{ params: { ps: 0 } }
			)
			if (res.status === 204) return fulfillWithValue({ workers: [], totalCount: 0 })
			return fulfillWithValue(res.data ?? { workers: [], totalCount: 0 })
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue(
				error?.response?.data?.message ?? "Error fetching workers"
			)
		}
	}
)

export const fetchWorkerPrograms = createAsyncThunk(
	"evaluations/fetchWorkerPrograms",
	async (workerId: string, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get<WorkerProgram[]>(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/GetWorkerPrograms/${workerId}`
			)
			if (res.status === 204) return fulfillWithValue([])
			return fulfillWithValue(res.data ?? [])
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue(
				error?.response?.data?.message ?? "Error fetching worker programs"
			)
		}
	}
)

// Eligibility/due date override on a single instance. Rejects with the HTTP
// status alongside the message: 400 (deadline before eligible), 403 (other
// company), 404 (gone) and 409 (already COMPLETED/CANCELLED) each need their
// own message and their own recovery in the dialog.
export const updateInstanceDates = createAsyncThunk(
	"evaluations/updateInstanceDates",
	async (payload: UpdateInstanceDatesPayload, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.post<UpdateInstanceDatesResponse>(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/UpdateInstanceDates`,
				payload
			)
			return fulfillWithValue(res.data)
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue({
				status: error?.response?.status ?? null,
				message: error?.response?.data?.message ?? "Error updating dates",
			})
		}
	}
)

export const fetchInstanceFormDetail = createAsyncThunk(
	"evaluations/fetchInstanceFormDetail",
	async (instanceFormId: number, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get<InstanceFormDetail>(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/GetInstanceFormDetails/${instanceFormId}`
			)
			return fulfillWithValue(res.data)
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue(
				error?.response?.data?.message ?? "Error fetching form details"
			)
		}
	}
)

// Same call as fetchInstanceFormDetail, kept as a separate thunk so its
// fulfilled/pending/rejected cases can write into fillFormDialog's
// counterpart slot without clobbering the primary row's detail.
export const fetchCounterpartInstanceFormDetail = createAsyncThunk(
	"evaluations/fetchCounterpartInstanceFormDetail",
	async (instanceFormId: number, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get<InstanceFormDetail>(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/GetInstanceFormDetails/${instanceFormId}`
			)
			return fulfillWithValue(res.data)
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue(
				error?.response?.data?.message ?? "Error fetching form details"
			)
		}
	}
)

export const saveFormAnswers = createAsyncThunk(
	"evaluations/saveFormAnswers",
	async (payload: SaveFormAnswersPayload, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.post<SaveFormAnswersResponse>(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/SaveFormAnswers`,
				payload
			)
			return fulfillWithValue(res.data)
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue(
				error?.response?.data?.message ?? "Error saving form answers"
			)
		}
	}
)

export const calculateScore = createAsyncThunk(
	"evaluations/calculateScore",
	async (payload: CalculateScorePayload, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.post<CalculateScoreResponse>(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/CalculateScore`,
				payload
			)
			return fulfillWithValue(res.data)
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue(
				error?.response?.data?.message ?? "Error calculating score"
			)
		}
	}
)

export const saveAdditionalFieldAnswers = createAsyncThunk(
	"evaluations/saveAdditionalFieldAnswers",
	async (payload: SaveAdditionalFieldAnswersPayload, { fulfillWithValue, rejectWithValue }) => {
		try {
			await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/SaveAdditionalFieldAnswers`,
				payload
			)
			return fulfillWithValue(undefined)
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue(
				error?.response?.data?.message ?? "Error saving additional field answers"
			)
		}
	}
)

export const fetchProgramInstanceDetails = createAsyncThunk(
	"evaluations/fetchProgramInstanceDetails",
	async (instanceId: number, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get<ProgramInstanceDetail>(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/GetProgramInstanceDetails/${instanceId}`
			)
			return fulfillWithValue(res.data)
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue(
				error?.response?.data?.message ?? "Error fetching program details"
			)
		}
	}
)

export const fetchMyAcknowledgements = createAsyncThunk(
	"evaluations/fetchMyAcknowledgements",
	async (isPending: boolean | undefined, { fulfillWithValue, rejectWithValue }) => {
		try {
			const params: Record<string, unknown> = {}
			if (isPending !== undefined) params.isPending = isPending
			const res = await axios.get<AcknowledgementListResponse>(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/GetMyAcknowledgements`,
				{ params }
			)
			if (res.status === 204) return fulfillWithValue({ items: [], totalCount: 0 })
			return fulfillWithValue(res.data ?? { items: [], totalCount: 0 })
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue(
				error?.response?.data?.message ?? "Error fetching acknowledgements"
			)
		}
	}
)

// Resolves, for a set of program instances, which respondent (the manager
// themself or the worker) each pending-acknowledgement instanceFormId belongs
// to — GetMyAcknowledgements doesn't carry that distinction, but each
// program's forms[] gives the definitive userInstanceFormId/workerInstanceFormId
// mapping, so the "My Acknowledgements" table can label rows accurately
// instead of leaving duplicate-looking entries unexplained.
export const fetchAcknowledgementRowTypes = createAsyncThunk(
	"evaluations/fetchAcknowledgementRowTypes",
	async (instanceIds: number[], { fulfillWithValue }) => {
		const results = await Promise.all(
			instanceIds.map(async (instanceId) => {
				try {
					const res = await axios.get<ProgramInstanceDetail>(
						`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/GetProgramInstanceDetails/${instanceId}`
					)
					return res.data
				} catch {
					return null
				}
			})
		)
		const rowTypes: Record<number, "USER" | "WORKER"> = {}
		results.forEach((detail) => {
			detail?.forms.forEach((form) => {
				if (form.userInstanceFormId !== null) {
					rowTypes[form.userInstanceFormId] = "USER"
				}
				if (form.workerInstanceFormId !== null) {
					rowTypes[form.workerInstanceFormId] = "WORKER"
				}
			})
		})
		return fulfillWithValue(rowTypes)
	}
)

export const fetchAcknowledgeFormDetail = createAsyncThunk(
	"evaluations/fetchAcknowledgeFormDetail",
	async (instanceFormId: number, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get<InstanceFormDetail>(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/GetInstanceFormDetails/${instanceFormId}`
			)
			return fulfillWithValue(res.data)
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue(
				error?.response?.data?.message ?? "Error fetching form details"
			)
		}
	}
)

// Plain acknowledgements keep today's JSON body; a signed one adds the PNG
// as bare base64 in `signatureImageBase64` (GAT-7790 — required when the
// step has requiresSignature, ignored otherwise).
async function buildAcknowledgeBody({
	instanceFormId,
	roleId,
	comment,
	signature,
}: AcknowledgeFormPayload) {
	if (!signature) return { instanceFormId, roleId, comment }
	return {
		instanceFormId,
		roleId,
		comment,
		signatureImageBase64: await blobToBase64(signature),
	}
}

export const acknowledgeForm = createAsyncThunk<
	AcknowledgeFormResponse,
	AcknowledgeFormPayload,
	{ rejectValue: AcknowledgeFormError }
>(
	"evaluations/acknowledgeForm",
	async (payload, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.post<AcknowledgeFormResponse>(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/AcknowledgeForm`,
				await buildAcknowledgeBody(payload)
			)
			return fulfillWithValue(res.data)
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue({
				status: error?.response?.status ?? null,
				message: error?.response?.data?.message ?? null,
			})
		}
	}
)

// Re-reads one open row of the View Form dialog in place (no loading state),
// so a just-signed step picks up its signatureImageUrl and signer name.
export const refreshFillFormRow = createAsyncThunk(
	"evaluations/refreshFillFormRow",
	async (instanceFormId: number, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.get<InstanceFormDetail>(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluationInstance/GetInstanceFormDetails/${instanceFormId}`
			)
			return fulfillWithValue(res.data)
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue(
				error?.response?.data?.message ?? "Error fetching form details"
			)
		}
	}
)

export const fetchEvaluationFilters = createAsyncThunk(
	"evaluations/fetchEvaluationFilters",
	async (_, { fulfillWithValue, rejectWithValue }) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/FlexScheduler/GetFiltersForUser`,
				{ filters: EVALUATIONS_FILTERS }
			)
			if (res.status === 204) return fulfillWithValue(null)
			return fulfillWithValue(res.data)
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue(
				error?.response?.data?.message ?? "Error fetching filters"
			)
		}
	}
)

const evaluationsSlice = createSlice({
	name: "evaluations",
	initialState,
	reducers: {
		setSearchText(state, action) {
			state.searchText = action.payload
		},
		clearWorkerPrograms(state) {
			state.workerPrograms = { ...initialState.workerPrograms }
		},
		clearProgramInstanceDetail(state) {
			state.programInstanceDetail = { ...initialState.programInstanceDetail }
		},
		openFillFormDialog(state, action: { payload: { instanceFormId: number; counterpartInstanceFormId?: number | null; workerName: string | null; workCenterName: string | null; forceReadOnly?: boolean } }) {
			state.fillFormDialog.isOpen = true
			state.fillFormDialog.instanceFormId = action.payload.instanceFormId
			state.fillFormDialog.workerName = action.payload.workerName
			state.fillFormDialog.workCenterName = action.payload.workCenterName
			state.fillFormDialog.detail = null
			state.fillFormDialog.status = "idle"
			state.fillFormDialog.saveStatus = "idle"
			state.fillFormDialog.saveError = null
			state.fillFormDialog.forceReadOnly = action.payload.forceReadOnly ?? false
			state.fillFormDialog.counterpartInstanceFormId = action.payload.counterpartInstanceFormId ?? null
			state.fillFormDialog.counterpartDetail = null
			state.fillFormDialog.counterpartStatus = "idle"
			state.fillFormDialog.scoreCalculation = {
				status: "idle",
				score: null,
				targetValue: null,
				metTarget: null,
			}
		},
		closeFillFormDialog(state) {
			state.fillFormDialog = { ...initialState.fillFormDialog }
		},
		resetSaveStatus(state) {
			state.fillFormDialog.saveStatus = "idle"
			state.fillFormDialog.saveAction = null
			state.fillFormDialog.saveError = null
		},
		openAdjustDatesDialog(
			state,
			action: {
				payload: { instance: WorkerProgram; workerName: string | null }
			}
		) {
			state.adjustDatesDialog = {
				...initialState.adjustDatesDialog,
				isOpen: true,
				instance: action.payload.instance,
				workerName: action.payload.workerName,
			}
		},
		closeAdjustDatesDialog(state) {
			state.adjustDatesDialog = { ...initialState.adjustDatesDialog }
		},
		resetAdjustDatesSaveStatus(state) {
			state.adjustDatesDialog.saveStatus = "idle"
			state.adjustDatesDialog.error = null
		},
		openAcknowledgeDialog(
			state,
			action: { payload: { instanceFormId: number; workerName: string | null } }
		) {
			state.acknowledgeDialog.isOpen = true
			state.acknowledgeDialog.instanceFormId = action.payload.instanceFormId
			state.acknowledgeDialog.workerName = action.payload.workerName
			state.acknowledgeDialog.detail = null
			state.acknowledgeDialog.status = "idle"
			state.acknowledgeDialog.saveStatus = "idle"
			state.acknowledgeDialog.isSigned = false
			state.acknowledgeDialog.saveErrorStatus = null
		},
		closeAcknowledgeDialog(state) {
			// The save outcome is kept (reset on the next open): the dialog
			// closes in the same tick the acknowledgement lands, and the tab's
			// toast still has to see it.
			state.acknowledgeDialog = {
				...state.acknowledgeDialog,
				isOpen: false,
				instanceFormId: null,
				workerName: null,
				detail: null,
				status: "idle",
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchWorkers.pending, (state) => {
				state.workers.status = "pending"
			})
			.addCase(fetchWorkers.fulfilled, (state, action) => {
				state.workers.status = "fulfilled"
				state.workers.data = action.payload.workers
				state.workers.totalCount = action.payload.totalCount
			})
			.addCase(fetchWorkers.rejected, (state, action: any) => { // eslint-disable-line
				state.workers.status = "rejected"
				state.workers.error =
					action.payload ?? action.error?.message ?? "An error occurred"
			})
			.addCase(fetchWorkerPrograms.pending, (state) => {
				state.workerPrograms.status = "pending"
			})
			.addCase(fetchWorkerPrograms.fulfilled, (state, action) => {
				state.workerPrograms.status = "fulfilled"
				state.workerPrograms.data = action.payload
			})
			.addCase(fetchWorkerPrograms.rejected, (state, action: any) => { // eslint-disable-line
				state.workerPrograms.status = "rejected"
				state.workerPrograms.error =
					action.payload ?? action.error?.message ?? "An error occurred"
			})
			.addCase(updateInstanceDates.pending, (state) => {
				state.adjustDatesDialog.saveStatus = "pending"
				state.adjustDatesDialog.error = null
			})
			.addCase(updateInstanceDates.fulfilled, (state, action) => {
				state.adjustDatesDialog.saveStatus = "fulfilled"
				// Patch the row behind the dialog so the list reflects the new
				// dates immediately; the full re-read follows from the caller.
				const updated = action.payload
				const target = state.workerPrograms.data.find(
					(i) => i.instanceId === updated.instanceId
				)
				if (target) {
					target.eligibleDate = updated.eligibleDate
					target.deadlineDate = updated.deadlineDate
					target.statusId = updated.statusId
					target.statusCode = updated.statusCode
				}
			})
			.addCase(updateInstanceDates.rejected, (state, action: any) => { // eslint-disable-line
				state.adjustDatesDialog.saveStatus = "rejected"
				state.adjustDatesDialog.error = action.payload ?? {
					status: null,
					message: action.error?.message ?? "An error occurred",
				}
			})
			.addCase(fetchProgramInstanceDetails.pending, (state) => {
				state.programInstanceDetail.status = "pending"
			})
			.addCase(fetchProgramInstanceDetails.fulfilled, (state, action) => {
				state.programInstanceDetail.status = "fulfilled"
				state.programInstanceDetail.data = action.payload
			})
			.addCase(fetchProgramInstanceDetails.rejected, (state, action: any) => { // eslint-disable-line
				state.programInstanceDetail.status = "rejected"
				state.programInstanceDetail.error =
					action.payload ?? action.error?.message ?? "An error occurred"
			})
			.addCase(fetchInstanceFormDetail.pending, (state) => {
				state.fillFormDialog.status = "pending"
			})
			.addCase(fetchInstanceFormDetail.fulfilled, (state, action) => {
				state.fillFormDialog.status = "fulfilled"
				state.fillFormDialog.detail = action.payload
			})
			.addCase(fetchInstanceFormDetail.rejected, (state) => {
				state.fillFormDialog.status = "rejected"
			})
			.addCase(fetchCounterpartInstanceFormDetail.pending, (state) => {
				state.fillFormDialog.counterpartStatus = "pending"
			})
			.addCase(fetchCounterpartInstanceFormDetail.fulfilled, (state, action) => {
				state.fillFormDialog.counterpartStatus = "fulfilled"
				state.fillFormDialog.counterpartDetail = action.payload
			})
			.addCase(fetchCounterpartInstanceFormDetail.rejected, (state) => {
				state.fillFormDialog.counterpartStatus = "rejected"
			})
			.addCase(saveFormAnswers.pending, (state, action) => {
				state.fillFormDialog.saveStatus = "pending"
				state.fillFormDialog.saveAction = action.meta.arg.isFinal ? "submit" : "draft"
				state.fillFormDialog.saveError = null
			})
			.addCase(saveFormAnswers.fulfilled, (state, action) => {
				state.fillFormDialog.saveStatus = "fulfilled"
				const detail = state.fillFormDialog.detail
				const isWorkerSide = detail?.editableRespondentType === "WORKER"
				if (detail) {
					if (isWorkerSide) {
						detail.workerStatusId = action.payload.formStatusId
						detail.workerStatusCode = action.payload.formStatusCode
						detail.workerStatusName = action.payload.formStatusName
					} else {
						detail.userStatusId = action.payload.formStatusId
						detail.userStatusCode = action.payload.formStatusCode
						detail.userStatusName = action.payload.formStatusName
					}
				}
				if (state.programInstanceDetail.data) {
					const requestedId = state.fillFormDialog.instanceFormId
					const form = state.programInstanceDetail.data.forms.find(
						f => f.userInstanceFormId === requestedId || f.workerInstanceFormId === requestedId
					)
					if (form) {
						if (isWorkerSide) {
							form.workerStatusId = action.payload.formStatusId
							form.workerStatusCode = action.payload.formStatusCode
							form.workerStatusName = action.payload.formStatusName
						} else {
							form.userStatusId = action.payload.formStatusId
							form.userStatusCode = action.payload.formStatusCode
							form.userStatusName = action.payload.formStatusName
						}
					}
				}
			})
			.addCase(saveFormAnswers.rejected, (state, action) => {
				state.fillFormDialog.saveStatus = "rejected"
				state.fillFormDialog.saveError =
					(action.payload as string | undefined) ?? null
			})
			.addCase(calculateScore.pending, (state) => {
				state.fillFormDialog.scoreCalculation.status = "pending"
			})
			.addCase(calculateScore.fulfilled, (state, action) => {
				state.fillFormDialog.scoreCalculation.status = "fulfilled"
				state.fillFormDialog.scoreCalculation.score = action.payload.score
				state.fillFormDialog.scoreCalculation.targetValue = action.payload.targetValue
				state.fillFormDialog.scoreCalculation.metTarget = action.payload.metTarget
			})
			.addCase(calculateScore.rejected, (state) => {
				state.fillFormDialog.scoreCalculation.status = "rejected"
			})
			.addCase(saveAdditionalFieldAnswers.pending, (state) => {
				state.saveAdditionalFieldsStatus = "pending"
			})
			.addCase(saveAdditionalFieldAnswers.fulfilled, (state) => {
				state.saveAdditionalFieldsStatus = "fulfilled"
			})
			.addCase(saveAdditionalFieldAnswers.rejected, (state) => {
				state.saveAdditionalFieldsStatus = "rejected"
			})
			.addCase(fetchMyAcknowledgements.pending, (state) => {
				state.myAcknowledgements.status = "pending"
			})
			.addCase(fetchMyAcknowledgements.fulfilled, (state, action) => {
				state.myAcknowledgements.status = "fulfilled"
				state.myAcknowledgements.data = action.payload.items
				state.myAcknowledgements.totalCount = action.payload.totalCount
			})
			.addCase(fetchMyAcknowledgements.rejected, (state, action: any) => { // eslint-disable-line
				state.myAcknowledgements.status = "rejected"
				state.myAcknowledgements.error =
					action.payload ?? action.error?.message ?? "An error occurred"
			})
			.addCase(fetchAcknowledgementRowTypes.fulfilled, (state, action) => {
				state.ackRowTypes = { ...state.ackRowTypes, ...action.payload }
			})
			.addCase(fetchAcknowledgeFormDetail.pending, (state) => {
				state.acknowledgeDialog.status = "pending"
			})
			.addCase(fetchAcknowledgeFormDetail.fulfilled, (state, action) => {
				state.acknowledgeDialog.status = "fulfilled"
				state.acknowledgeDialog.detail = action.payload
			})
			.addCase(fetchAcknowledgeFormDetail.rejected, (state) => {
				state.acknowledgeDialog.status = "rejected"
			})
			.addCase(acknowledgeForm.pending, (state, action) => {
				state.acknowledgeDialog.saveStatus = "pending"
				state.acknowledgeDialog.isSigned = Boolean(action.meta.arg.signature)
				state.acknowledgeDialog.saveErrorStatus = null
			})
			.addCase(acknowledgeForm.fulfilled, (state, action) => {
				state.acknowledgeDialog.saveStatus = "fulfilled"
				state.acknowledgeDialog.isSigned = Boolean(action.meta.arg.signature)
				const { instanceFormId, roleId, comment } = action.meta.arg
				const {
					formStatusId,
					formStatusCode,
					formStatusName,
					instanceStatusId,
					instanceStatusCode,
					instanceStatusName,
				} = action.payload

				// Update the acknowledged step + form status in the open dialog detail
				// — the acknowledged instanceFormId may be either the primary row or
				// its counterpart (GAT-7449: both rows can be acknowledged from the
				// same dialog), so check both slots.
				const applyAckPatch = (detail: typeof state.fillFormDialog.detail) => {
					if (!detail || detail.instanceFormId !== instanceFormId) return
					const step = detail.acknowledgementWorkflow?.steps.find(
						s => s.assigneeRoleId === roleId && !s.isAcknowledged
					)
					if (step) {
						step.isAcknowledged = true
						step.acknowledgementDate = new Date().toISOString()
						step.comment = comment ?? null
					}
					// The name too, not just the code: the status chip takes its
					// colour from the code and its label from the name, so patching
					// only the code left a green "Acknowledgement Pending" until
					// the dialog was reopened (GAT-7826).
					if (detail.editableRespondentType === "WORKER") {
						detail.workerStatusId = formStatusId
						detail.workerStatusCode = formStatusCode
						detail.workerStatusName = formStatusName
					} else {
						detail.userStatusId = formStatusId
						detail.userStatusCode = formStatusCode
						detail.userStatusName = formStatusName
					}
				}
				applyAckPatch(state.fillFormDialog.detail)
				applyAckPatch(state.fillFormDialog.counterpartDetail)

				// The row's completed-acknowledgement count, recomputed from the
				// workflow applyAckPatch just updated. The Ack. Progress column
				// reads the *AcknowledgementsCompleted fields below, which nothing
				// here used to touch — so the column sat stale (0/4 after signing)
				// until a re-entry refetched the instance (GAT-7584). Derived from
				// the step data rather than incremented, so a repeat can't inflate
				// it. Null when neither dialog slot holds this row (e.g. the My
				// Acknowledgements tab, where this grid isn't mounted anyway), in
				// which case the counts are left for the next fetch.
				const patchedDetail =
					state.fillFormDialog.detail?.instanceFormId === instanceFormId
						? state.fillFormDialog.detail
						: state.fillFormDialog.counterpartDetail?.instanceFormId ===
							instanceFormId
							? state.fillFormDialog.counterpartDetail
							: null
				const workflow = patchedDetail?.acknowledgementWorkflow
				const completedAckCount = workflow
					? workflow.steps.filter(
						s => s.stepActionCode === "ACKNOWLEDGE" && s.isAcknowledged
					).length
					: null

				// Update the matching form row + overall instance status
				const programDetail = state.programInstanceDetail.data
				if (programDetail) {
					const form = programDetail.forms.find(
						f => f.userInstanceFormId === instanceFormId || f.workerInstanceFormId === instanceFormId
					)
					if (form) {
						if (form.workerInstanceFormId === instanceFormId) {
							form.workerStatusId = formStatusId
							form.workerStatusCode = formStatusCode
							form.workerStatusName = formStatusName
							if (completedAckCount !== null) {
								form.workerAcknowledgementsCompleted = completedAckCount
							}
						} else {
							form.userStatusId = formStatusId
							form.userStatusCode = formStatusCode
							form.userStatusName = formStatusName
							if (completedAckCount !== null) {
								form.userAcknowledgementsCompleted = completedAckCount
							}
						}
					}
					programDetail.statusId = instanceStatusId
					programDetail.statusCode = instanceStatusCode
					programDetail.statusName = instanceStatusName
				}

				// Update the worker programs list status for this instance
				if (programDetail) {
					const program = state.workerPrograms.data.find(
						p => p.instanceId === programDetail.instanceId
					)
					if (program) {
						program.statusId = instanceStatusId
						program.statusCode = instanceStatusCode
						program.statusName = instanceStatusName
					}
				}
			})
			.addCase(acknowledgeForm.rejected, (state, action) => {
				state.acknowledgeDialog.saveStatus = "rejected"
				state.acknowledgeDialog.saveErrorStatus = action.payload?.status ?? null
			})
			.addCase(refreshFillFormRow.fulfilled, (state, action) => {
				const { instanceFormId } = action.payload
				if (state.fillFormDialog.detail?.instanceFormId === instanceFormId) {
					state.fillFormDialog.detail = action.payload
				} else if (
					state.fillFormDialog.counterpartDetail?.instanceFormId === instanceFormId
				) {
					state.fillFormDialog.counterpartDetail = action.payload
				}
			})
			.addCase(fetchEvaluationFilters.pending, (state) => {
				state.evaluationFilters.status = "pending"
				state.evaluationFilters.error = ""
			})
			.addCase(fetchEvaluationFilters.fulfilled, (state, action) => {
				state.evaluationFilters.status = "fulfilled"
				if (action.payload) {
					const { locations, workCenters, managers, filterRelationships } = action.payload
					const sortByName = (a: { name: string }, b: { name: string }) =>
						a.name?.trim().localeCompare(b.name?.trim())
					state.evaluationFilters.data = {
						locations: (locations ?? []).slice().sort(sortByName),
						workCenters: (workCenters ?? []).slice().sort(sortByName),
						managers: (managers ?? []).slice().sort(sortByName),
						filterRelationships: filterRelationships ?? [],
					}
				}
			})
			.addCase(fetchEvaluationFilters.rejected, (state, action: any) => { // eslint-disable-line
				state.evaluationFilters.status = "rejected"
				state.evaluationFilters.error =
					action.payload ?? action.error?.message ?? "An error occurred"
			})
	},
})

export const {
	setSearchText,
	clearWorkerPrograms,
	clearProgramInstanceDetail,
	openFillFormDialog,
	closeFillFormDialog,
	resetSaveStatus,
	openAdjustDatesDialog,
	closeAdjustDatesDialog,
	resetAdjustDatesSaveStatus,
	openAcknowledgeDialog,
	closeAcknowledgeDialog,
} = evaluationsSlice.actions

export default evaluationsSlice.reducer
